import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import {
  FileEntity,
  FileStatus,
} from "@quantizr-engineering/common/types/file";
import DropzoneOverlay from "./DropzoneOverlay";
import { FaTrash } from "react-icons/fa";
import GradientButton from "./GradientButton";
import { toast } from "sonner";
import { ContractType } from "@quantizr-engineering/common/types/show";
import { useShowService } from "../api/showService";
import { getShowDate } from "../utils/dateFormatters.ts";
import { Formik, Form, FieldArray, Field, FieldInputProps } from "formik";
import * as Yup from "yup";
import { useRecordedService } from "../api/recordedService";
import { useFileService } from "../api/fileService.ts";
import { useRunService } from "../api/runService.ts";
import SelectCell from "./SelectCell.tsx";
import { isShowInActualStatus } from "../utils/showStatus.ts";

type FileModalProps = {
  initialShowId?: number;
  initialRecordingId?: number;
  initialRunId?: number;
  onClose: (fileEntities: FileEntity[]) => void;
  defaultType?: ContractType;
  initialFiles?: Partial<FileEntity>[];
};

const FileModal: React.FC<FileModalProps> = ({
  initialShowId = null,
  initialRecordingId = null,
  initialRunId = null,
  defaultType = null,
  onClose,
  initialFiles = [],
}) => {
  const [fileUploads, setFileUploads] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { shows } = useShowService();
  const { recorded } = useRecordedService();
  const { fileEntities, uploadArbitrary, fileEntitiesLoading } =
    useFileService();
  const { runs } = useRunService();
  const [isUploading, setIsUploading] = useState(false);

  const contractTypesList = [
    ContractType.SHOW_OFFER,
    ContractType.LIVE_MUSIC_AGREEMENT,
    ContractType.LIVE_MUSIC_SETTLEMENT,
    ContractType.RECORDING_COLLABORATION_AGREEMENT,
    ContractType.BRAND_DEAL,
    ContractType.INSURANCE_CONTRACT,
    ContractType.RECORDING_RECORD_LABEL_AGREEMENT,
    ContractType.MISC,
  ];

  const getCustomInputOptions = useMemo(
    () => (file: Partial<FileEntity>) => {
      switch (file.contractType) {
        case ContractType.SHOW_OFFER:
          return [
            {
              value: -1,
              label: "Create New Show",
            },
          ];
        case ContractType.LIVE_MUSIC_AGREEMENT:
          return [
            {
              value: -1,
              label: "Create New Show",
            },
          ].concat(
            shows
              ?.filter((s) => !isShowInActualStatus(s))
              .map((show) => ({
                value: show.id ?? -1,
                label: `${getShowDate(show)} @ ${show.venue ?? "TBD"}`,
              }))
              .sort((a, b) => a.label.localeCompare(b.label)) ?? []
          );
        case ContractType.LIVE_MUSIC_SETTLEMENT:
        case ContractType.MISC:
          return (
            shows?.map((show) => ({
              value: show.id ?? -1,
              label: `${getShowDate(show)} @ ${show.venue ?? "TBD"}`,
            })) ?? []
          );
        case ContractType.RECORDING_COLLABORATION_AGREEMENT:
          return (
            recorded
              ?.flatMap((release) => release.recordings)
              .filter((recording) => !!recording.title)
              ?.map((recording) => ({
                value: recording.id ?? -1,
                label: recording.title,
              })) ?? []
          );

        default:
          return [];
      }
    },
    [shows, recorded]
  );

  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!!fileEntitiesLoading) return;

    let files: Partial<FileEntity>[] = [];

    interface FileWithShows extends FileEntity {
      shows: { id: number }[];
    }

    interface FileWithRecordings extends FileEntity {
      recordings: { id: number }[];
    }

    // Handle existing files from fileEntities
    if (initialShowId) {
      files =
        fileEntities?.filter((fe) =>
          (fe as FileWithShows).shows.some((s) => s.id === initialShowId)
        ) ?? [];
    } else if (initialRecordingId) {
      files =
        fileEntities?.filter((fe) =>
          (fe as FileWithRecordings).recordings.some(
            (r) => r.id === initialRecordingId
          )
        ) ?? [];
    }

    setReady(true);
  }, [initialShowId, fileEntities]);

  useEffect(() => {
    setFileUploads(initialFiles as File[]);
  }, []);

  const handleMapNewFilesToFileEntities = useCallback(
    async (files: File[], push: any) => {
      const uniqueFiles = files.filter(
        (file) =>
          !fileUploads.some((existingFile) => existingFile.name === file.name)
      );

      if (push) {
        for (const file of uniqueFiles) {
          push({
            ...file,
            name: file.name,
            filename: file.name,
            mimeType: file.type,
            contractType: defaultType,
            status: FileStatus.PENDING,
            uploadedAt: new Date(),
            showID: initialShowId,
            runID: initialRunId,
            recordingID: initialRecordingId,
          });
        }
        setFileUploads([...uniqueFiles, ...fileUploads]);
      }
    },
    [fileUploads, defaultType]
  );

  const validationSchema = Yup.object().shape({
    files: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("File name is required"),
        filename: Yup.string().required("Filename is required"),
        contractType: Yup.string()
          .oneOf(contractTypesList)
          .required("Contract type is required"),
        showID: Yup.number().when("contractType", {
          is: (type: string) =>
            type === ContractType.SHOW_OFFER ||
            type === ContractType.LIVE_MUSIC_AGREEMENT ||
            type === ContractType.LIVE_MUSIC_SETTLEMENT,
          then: () => Yup.number().required("Show is required"),
          otherwise: () => Yup.number().notRequired(),
        }),
        recordingID: Yup.number().when("contractType", {
          is: ContractType.RECORDING_COLLABORATION_AGREEMENT,
          then: () => Yup.number().required("Recording is required"),
          otherwise: () => Yup.number().notRequired(),
        }),
        runID: Yup.number().when("showID", {
          is: -1,
          then: () => Yup.number().required("Run is required"),
          otherwise: () => Yup.number().notRequired(),
        }),
      })
    ),
  });

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    push: any
  ) => {
    if (event.target.files) {
      handleMapNewFilesToFileEntities(Array.from(event.target.files), push);
    }
  };

  const handleBoxClick = () => {
    fileInputRef.current?.click();
  };

  if (!ready) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50">
        <div className="fixed inset-0">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="bg-gray-100 p-4 rounded-lg">
              <h3 className="text-lg font-semibold">Loading files...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50">
      <div className="fixed inset-0" onClick={() => onClose([])}>
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-6xl h-[80vh]"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => onClose([])}
            className="absolute top-2 right-4 text-black font-bold hover:text-gray-600"
          >
            X
          </button>

          <div className="bg-gray-100 p-4 h-full pb-16 rounded-lg">
            <h3 className="text-lg font-semibold mb-3">Upload Files</h3>
            <div className="bg-white shadow-md rounded-lg overflow-hidden h-full flex flex-col">
              <Formik
                initialValues={{
                  files: initialFiles.map((file) => ({
                    ...file,
                    filename: file.name,
                    contractType: file.contractType ?? defaultType,
                    status: FileStatus.PENDING,
                    uploadedAt: new Date(),
                    showID:
                      initialShowId ?? file.shows?.[0].id ?? file.show ?? -1,
                    recordingID:
                      initialRecordingId ??
                      file?.recordings?.[0]?.id ??
                      file?.recording ??
                      null,
                    runID: initialRunId ?? null,
                  })),
                }}
                enableReinitialize={false}
                validationSchema={validationSchema}
                onSubmit={async (values, { setSubmitting }) => {
                  setIsUploading(true);
                  try {
                    const res = await uploadArbitrary(
                      fileUploads,
                      values.files
                    );
                    onClose(res);
                  } catch (error) {
                    toast.error("Upload failed");
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {({
                  values,
                  handleChange,
                  dirty,
                  handleSubmit,
                  errors,
                  setFieldValue,
                  touched,
                }) => {
                  return (
                    <Form
                      onSubmit={handleSubmit}
                      className="flex flex-col w-full min-w-0 space-y-4 p-4 h-full"
                    >
                      <div
                        onClick={handleBoxClick}
                        className="mb-4 py-4 border-2 border-dashed border-gray-200 text-center cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                      >
                        <p className="text-gray-500">
                          Click here or drag files to upload files
                        </p>
                      </div>
                      <div className="border-t flex-1 overflow-y-auto">
                        <div className="grid grid-cols-12 gap-4 p-4 border-b bg-gray-50">
                          <div className="col-span-5">File Name</div>
                          <div className="col-span-3">Type</div>
                          <div className="col-span-3">Selection</div>
                          <div className="col-span-1">Actions</div>
                        </div>
                        <div>
                          <FieldArray name="files">
                            {({ push, remove }) => (
                              <>
                                <DropzoneOverlay
                                  onDrop={(files) => {
                                    handleMapNewFilesToFileEntities(
                                      files,
                                      push
                                    );
                                  }}
                                />

                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  className="hidden"
                                  multiple
                                  onChange={(e) => handleInputChange(e, push)}
                                />

                                {values.files.map((file, index) => (
                                  <div
                                    key={`${file.id}-${index}`}
                                    className="grid grid-cols-12 gap-4 p-4 border-b items-center"
                                  >
                                    <div className="col-span-5 truncate">
                                      {file.name || file.filename}
                                    </div>
                                    <div className="col-span-3">
                                      <Field
                                        name={`files.${index}.contractType`}
                                      >
                                        {({
                                          field,
                                        }: {
                                          field: FieldInputProps<any>;
                                        }) => {
                                          return (
                                            <SelectCell
                                              {...field}
                                              label="Contract Type"
                                              value={file.contractType}
                                              options={contractTypesList.map(
                                                (type) => ({
                                                  value: type,
                                                  label: type,
                                                })
                                              )}
                                            />
                                          );
                                        }}
                                      </Field>
                                    </div>
                                    <div className="col-span-3">
                                      {(file.contractType ===
                                        ContractType.SHOW_OFFER ||
                                        file.contractType ===
                                          ContractType.LIVE_MUSIC_AGREEMENT ||
                                        file.contractType ===
                                          ContractType.LIVE_MUSIC_SETTLEMENT ||
                                        file.contractType ===
                                          ContractType.MISC) && (
                                        <Field name={`files[${index}].showID`}>
                                          {({
                                            field,
                                          }: {
                                            field: FieldInputProps<any>;
                                          }) => {
                                            return (
                                              <>
                                                <SelectCell
                                                  {...field}
                                                  label="Show"
                                                  value={file.showID ?? -1}
                                                  disableSort={true}
                                                  options={getCustomInputOptions(
                                                    file
                                                  ).map((option) => ({
                                                    value: option.value,
                                                    label:
                                                      option.label ||
                                                      String(option.value),
                                                  }))}
                                                  onChange={(e) => {
                                                    setFieldValue(
                                                      `files[${index}].showID`,
                                                      e.target.value
                                                    );
                                                  }}
                                                />
                                              </>
                                            );
                                          }}
                                        </Field>
                                      )}
                                      {file.contractType ===
                                        ContractType.RECORDING_COLLABORATION_AGREEMENT && (
                                        <Field
                                          name={`files[${index}].recordingID`}
                                        >
                                          {({
                                            field,
                                          }: {
                                            field: FieldInputProps<any>;
                                          }) => {
                                            return (
                                              <SelectCell
                                                {...field}
                                                // searchable={true}
                                                label="Recording"
                                                value={
                                                  file.recordingID ?? undefined
                                                }
                                                disableSort={true}
                                                onChange={(e) => {
                                                  setFieldValue(
                                                    `files[${index}].recordingID`,
                                                    parseInt(e.target.value)
                                                  );
                                                }}
                                                options={getCustomInputOptions(
                                                  file
                                                ).map((option) => ({
                                                  value: String(option.value),
                                                  label:
                                                    option.label ||
                                                    String(option.value),
                                                }))}
                                              />
                                            );
                                          }}
                                        </Field>
                                      )}
                                      {(file.contractType ===
                                        ContractType.SHOW_OFFER ||
                                        file.contractType ===
                                          ContractType.LIVE_MUSIC_AGREEMENT) &&
                                        file.showID === -1 && (
                                          <Field name={`files[${index}].runID`}>
                                            {({
                                              field,
                                            }: {
                                              field: FieldInputProps<any>;
                                            }) => {
                                              return (
                                                <SelectCell
                                                  {...field}
                                                  label="Run"
                                                  value={
                                                    file.runID ?? undefined
                                                  }
                                                  onChange={handleChange}
                                                  disableSort={true}
                                                  options={[
                                                    {
                                                      value: -1,
                                                      label: "Create New Run",
                                                    },
                                                  ].concat(
                                                    runs
                                                      ?.map((run) => ({
                                                        value: run.id ?? -1,
                                                        label: run.name,
                                                      }))
                                                      .sort((a, b) =>
                                                        a.label.localeCompare(
                                                          b.label
                                                        )
                                                      ) ?? []
                                                  )}
                                                />
                                              );
                                            }}
                                          </Field>
                                        )}
                                    </div>
                                    <div className="col-span-1 flex justify-center">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          remove(index);
                                          setFileUploads(
                                            fileUploads.filter(
                                              (f: File) => f.name !== file.name
                                            )
                                          );
                                        }}
                                        className="text-gray-500 hover:text-red-500 transition-colors"
                                      >
                                        <FaTrash className="h-5 w-5 mt-4" />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </FieldArray>
                        </div>
                      </div>
                      <div className="mt-4 flex justify-end space-x-4">
                        {isUploading ? (
                          <GradientButton
                            text="Uploading..."
                            type="submit"
                            disabled={true}
                          />
                        ) : (
                          <GradientButton
                            text="Upload"
                            type="submit"
                            disabled={false}
                          />
                        )}
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileModal;
